import { useState, useEffect } from "react";
import api, { getApiErrorMessage } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Megaphone, Calendar, Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PublicationFeed from "../../components/PublicationFeed";
import CampaignDetailsDialog from "../../components/CampaignDetailsDialog";
import { Button } from "../../components/ui/button";
import { BookOpen, Trophy } from "lucide-react";
import { campaignRuleDescription } from "../../lib/campaigns";

const PartnerCampanhas = () => {
  const { user } = useAuth();
  const [campanhas, setCampanhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    fetchCampanhas();
  }, [user]);

  const fetchCampanhas = async () => {
    try {
      const response = await api.get("/campanhas?status=ativa");
      setCampanhas(response.data);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error(getApiErrorMessage(error, "Erro ao carregar campanhas."));
    } finally {
      setLoading(false);
    }
  };

  const isActive = (campanha) => {
    const now = new Date();
    const start = new Date(campanha.data_inicio);
    const end = new Date(campanha.data_fim);
    return now >= start && now <= end;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeCampanhas = campanhas.filter(isActive);
  const upcomingCampanhas = campanhas.filter(c => new Date(c.data_inicio) > new Date());

  return (
    <div className="min-w-0 space-y-6 animate-fade-in" data-testid="partner-campanhas">
      <h1 className="font-heading text-2xl font-bold text-slate-900">
        Campanhas
      </h1>

      {/* Active Campaigns */}
      <CampaignDetailsDialog campaign={details?.campaign} mode={details?.mode} onClose={() => setDetails(null)} />
      <PublicationFeed />
      <div>
        <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
          Ativas agora
        </h2>
        {activeCampanhas.length > 0 ? (
          <div className="space-y-4">
            {activeCampanhas.map((campanha) => (
              <div
                key={campanha.id}
                className="min-w-0 rounded-2xl border-l-4 border-success bg-white p-5 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-success/10">
                    <Megaphone className="h-6 w-6 text-success" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="break-words font-heading font-semibold text-slate-900">
                      {campanha.nome}
                    </h3>
                    <p className="mt-1 break-words text-sm text-slate-600">
                      {campanha.descricao}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1 text-slate-500">
                        <Calendar className="h-4 w-4" />
                        Até {new Date(campanha.data_fim).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    
                    {/* Rules preview */}
                    {campanha.regras_json?.rules?.length > 0 && (
                      <div className="mt-4 p-3 bg-slate-50 rounded-xl">
                        <p className="text-xs font-medium text-slate-500 uppercase mb-2">
                          Bônus disponíveis
                        </p>
                        <div className="space-y-1">
                          {campanha.regras_json.rules.map((rule, i) => (
                            <div key={i} className="flex min-w-0 items-start gap-2 text-sm">
                              <Coins className="h-4 w-4 text-yellow-500" />
                              <span className="min-w-0 break-words text-slate-700">
                                {campaignRuleDescription(rule)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2"><Button variant="outline" size="sm" onClick={() => setDetails({ campaign: campanha, mode: "rules" })}><BookOpen className="mr-2 h-4 w-4" />Ver regras</Button><Button variant="outline" size="sm" onClick={() => setDetails({ campaign: campanha, mode: "ranking" })}><Trophy className="mr-2 h-4 w-4" />Ranking</Button></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 rounded-2xl p-8 text-center text-slate-500">
            Nenhuma campanha ativa no momento
          </div>
        )}
      </div>

      {/* Upcoming Campaigns */}
      {upcomingCampanhas.length > 0 && (
        <div>
          <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">
            Em breve
          </h2>
          <div className="space-y-4">
            {upcomingCampanhas.map((campanha) => (
              <div
                key={campanha.id}
                className="min-w-0 rounded-2xl border-l-4 border-slate-300 bg-white p-5 opacity-75 shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Megaphone className="h-6 w-6 text-slate-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="break-words font-heading font-semibold text-slate-900">
                      {campanha.nome}
                    </h3>
                    <p className="mt-1 break-words text-sm text-slate-600">
                      {campanha.descricao}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1 text-slate-500">
                        <Calendar className="h-4 w-4" />
                        Começa em {new Date(campanha.data_inicio).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCampanhas;
